import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiChevronRight, FiChevronLeft, FiCheck } from 'react-icons/fi';

const TYPES = ['Waistcoat', 'Shirt', 'Kurta', 'Been', 'Collar'];

const initMeasurement = {
  fullNameLabel: '', deliveryDate: '', suitQuantity: 1, type: 'Shirt',
  length: '', shoulder: '', arm: '', armHole: '', neck: '', chest: '',
  waist: '', surround: '', pantLength: '', pantWaist: '', ankle: '', wrist: '', plate: '',
  doubleSewing: false, zip: false, stud: false, frontPocket: false, net: false,
  surroundType: 'Round', shalwarType: 'Simple', buttons: 'Simple', sidePocket: 'One',
  extraInformation: '', price: '', advance: '', discount: '', isPending: true
};

export default function AddCustomer({ editMode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({ name: '', phone: '', city: '' });
  const [measurement, setMeasurement] = useState(initMeasurement);
  const [styleImage, setStyleImage] = useState(null);

  useEffect(() => {
    if (editMode && id) {
      api.get(`/customers/${id}`).then(r => {
        const c = r.data.customer;
        setCustomer({ name: c.name, phone: c.phone, city: c.city || '' });
        if (c.measurements?.[0]) {
          const m = c.measurements[0];
          setMeasurement({ ...initMeasurement, ...m,
            deliveryDate: m.deliveryDate ? m.deliveryDate.split('T')[0] : '' });
        }
      });
    }
  }, [editMode, id]);

  const setM = (field, val) => setMeasurement(prev => ({
    ...prev,
    [field]: val,
    ...(field === 'price' || field === 'advance' || field === 'discount' ? {
      remaining: (field === 'price' ? parseFloat(val) : parseFloat(prev.price) || 0) -
                 (field === 'discount' ? parseFloat(val) : parseFloat(prev.discount) || 0) -
                 (field === 'advance' ? parseFloat(val) : parseFloat(prev.advance) || 0)
    } : {})
  }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const remaining = (parseFloat(measurement.price) || 0) - (parseFloat(measurement.discount) || 0) -
        (parseFloat(measurement.advance) || 0);
      const payload = {
        ...customer,
        measurements: [{ ...measurement, remaining, isPending: remaining > 0 }]
      };

      if (styleImage) {
        const fd = new FormData();
        fd.append('styleImage', styleImage);
        fd.append('data', JSON.stringify(payload));
        if (editMode) {
          await api.put(`/customers/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          await api.post('/customers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      } else {
        if (editMode) {
          await api.put(`/customers/${id}`, payload);
        } else {
          await api.post('/customers', payload);
        }
      }
      toast.success(editMode ? 'Customer updated!' : 'Customer added successfully!');
      navigate('/customers');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setLoading(false);
  };

  const MeasureField = ({ label, field }) => (
    <div className="measurement-field">
      <label>{label}</label>
      <input type="number" step="0.5" placeholder="0" value={measurement[field] || ''}
        onChange={e => setM(field, e.target.value)} />
    </div>
  );

  return (
    <div>
      {/* Step Indicator */}
      <div className="steps" style={{ marginBottom: 24 }}>
        <div className={`step ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`}>
          <div className="step-num">{step > 1 ? <FiCheck size={14}/> : '1'}</div>
          <span className="step-label">Customer Info</span>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-num">2</div>
          <span className="step-label">Measurements</span>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {step === 1 && (
            <div>
              <div className="form-section-title" style={{ fontSize:14, fontWeight:600, marginBottom:16 }}>Customer Details</div>
              <div className="form-row-3">
                <div className="form-group">
                  <label className="form-label">Full Name <span className="required">*</span></label>
                  <input className="form-control" placeholder="Customer name"
                    value={customer.name} onChange={e => setCustomer({...customer, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone <span className="required">*</span></label>
                  <input className="form-control" placeholder="+971 50 000 0000"
                    value={customer.phone} onChange={e => setCustomer({...customer, phone: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-control" placeholder="Shop"
                    value={customer.city} onChange={e => setCustomer({...customer, city: e.target.value})} />
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                <button className="btn btn-accent"
                  onClick={() => { if (!customer.name || !customer.phone) { toast.error('Name and phone required'); return; } setStep(2); }}>
                  Next <FiChevronRight size={15} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              {/* Basic Info */}
              <div className="form-section">
                <div className="form-section-title">Order Info</div>
                <div className="form-row-3">
                  <div className="form-group">
                    <label className="form-label">Cloth Label Name</label>
                    <input className="form-control" placeholder="Name for cloth"
                      value={measurement.fullNameLabel} onChange={e => setM('fullNameLabel', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Delivery Date</label>
                    <input type="date" className="form-control"
                      value={measurement.deliveryDate} onChange={e => setM('deliveryDate', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Suit Quantity</label>
                    <div className="number-stepper">
                      <button type="button" onClick={() => setM('suitQuantity', Math.max(1, (measurement.suitQuantity||1)-1))}>−</button>
                      <input type="number" value={measurement.suitQuantity || 1} onChange={e => setM('suitQuantity', e.target.value)} />
                      <button type="button" onClick={() => setM('suitQuantity', (measurement.suitQuantity||1)+1)}>+</button>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Type</label>
                  <div className="radio-group">
                    {TYPES.map(t => (
                      <div key={t} className={`radio-item ${measurement.type === t ? 'selected' : ''}`}
                        onClick={() => setM('type', t)}>{t}</div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Measurements */}
              <div className="form-section">
                <div className="form-section-title">Body Measurements (inches)</div>
                <div className="measurement-grid">
                  <MeasureField label="Length" field="length" />
                  <MeasureField label="Shoulder" field="shoulder" />
                  <MeasureField label="Arm" field="arm" />
                  <MeasureField label="Arm Hole" field="armHole" />
                  <MeasureField label="Neck" field="neck" />
                  <MeasureField label="Chest" field="chest" />
                  <MeasureField label="Waist" field="waist" />
                  <MeasureField label="Surround" field="surround" />
                  <MeasureField label="Pant Length" field="pantLength" />
                  <MeasureField label="Pant Waist" field="pantWaist" />
                  <MeasureField label="Ankle" field="ankle" />
                  <MeasureField label="Wrist" field="wrist" />
                  <MeasureField label="Plate" field="plate" />
                </div>
              </div>

              {/* Options */}
              <div className="form-section">
                <div className="form-section-title">Features</div>
                <div className="check-group" style={{ marginBottom:12 }}>
                  {[['doubleSewing','Double Sewing'],['zip','Zip'],['stud','Stud'],['frontPocket','Front Pocket'],['net','Net']].map(([k,l]) => (
                    <div key={k} className={`check-item ${measurement[k] ? 'checked' : ''}`}
                      onClick={() => setM(k, !measurement[k])}>
                      {measurement[k] ? '✓' : '○'} {l}
                    </div>
                  ))}
                </div>

                <div className="form-row-2" style={{ marginBottom:12 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Surround Type</label>
                    <div className="radio-group">
                      {['Round','Square'].map(v => (
                        <div key={v} className={`radio-item ${measurement.surroundType === v ? 'selected' : ''}`}
                          onClick={() => setM('surroundType', v)}>{v}</div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Shalwar Type</label>
                    <div className="radio-group">
                      {['Simple','Trouser'].map(v => (
                        <div key={v} className={`radio-item ${measurement.shalwarType === v ? 'selected' : ''}`}
                          onClick={() => setM('shalwarType', v)}>{v}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Buttons</label>
                    <div className="radio-group">
                      {['Simple','Stylish'].map(v => (
                        <div key={v} className={`radio-item ${measurement.buttons === v ? 'selected' : ''}`}
                          onClick={() => setM('buttons', v)}>{v}</div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Side Pocket</label>
                    <div className="radio-group">
                      {['One','Two','No Pocket'].map(v => (
                        <div key={v} className={`radio-item ${measurement.sidePocket === v ? 'selected' : ''}`}
                          onClick={() => setM('sidePocket', v)}>{v}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Style Image & Notes */}
              <div className="form-section">
                <div className="form-section-title">Style & Notes</div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Style Image</label>
                    <input type="file" className="form-control" accept="image/*"
                      onChange={e => setStyleImage(e.target.files[0])} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Extra Information</label>
                    <textarea className="form-control" rows={2} placeholder="Special instructions..."
                      value={measurement.extraInformation}
                      onChange={e => setM('extraInformation', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="form-section" style={{ background:'#fef3c7' }}>
                <div className="form-section-title">💰 Pricing (SAR)</div>
                <div className="form-row-3">
                  <div className="form-group">
                    <label className="form-label">Total Price</label>
                    <input type="number" className="form-control" placeholder="0.00"
                      value={measurement.price} onChange={e => setM('price', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Advance Paid</label>
                    <input type="number" className="form-control" placeholder="0.00"
                      value={measurement.advance} onChange={e => setM('advance', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Discount</label>
                    <input type="number" className="form-control" placeholder="0.00"
                      value={measurement.discount} onChange={e => setM('discount', e.target.value)} />
                  </div>
                </div>
                <div className="form-row-2" style={{ marginTop:12 }}>
                  <div className="form-group">
                    <label className="form-label">Remaining</label>
                    <input type="number" className="form-control"
                      value={((parseFloat(measurement.price)||0)-(parseFloat(measurement.discount)||0)-(parseFloat(measurement.advance)||0)).toFixed(2)}
                      readOnly style={{ background:'#fff3cd', fontWeight:600, color:'#92400e' }} />
                  </div>
                </div>
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <button className="btn btn-outline" onClick={() => setStep(1)}>
                  <FiChevronLeft size={15} /> Back
                </button>
                <button className="btn btn-accent btn-lg" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Saving...' : (editMode ? '✓ Update Customer' : '✓ Save Customer')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}